// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { Loader2, PlayCircle, CheckCircle2, XCircle, MinusCircle } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * UAT tab — runs the server-side CareRx UAT runner (which drives the real D1-D8 endpoints over loopback
 * HTTP as the current actor) and renders the pass/fail report. Admin-only on the backend.
 */
export default function CareRxUat() {
  const [report, setReport] = useState<api.CareRxUatReport | null>(null)
  const [running, setRunning] = useState(false)
  const [msg, setMsg] = useState('')

  const run = async () => {
    setRunning(true); setMsg(''); setReport(null)
    try { setReport(await api.runCareRxUat()) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setRunning(false) }
  }

  const icon = (s: string) => s === 'pass' ? <CheckCircle2 size={15} color="#15803d" />
    : s === 'fail' ? <XCircle size={15} color="#b91c1c" /> : <MinusCircle size={15} color="#9ca3af" />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={cardTitle}><PlayCircle size={16} /> CareRx UAT</div>
          <p style={{ ...muted, marginTop: 4 }}>Runs end-to-end checks against the live D1–D8 endpoints as the current user, then reports pass/fail.</p>
        </div>
        <button style={primaryBtn} disabled={running} onClick={run}>
          {running ? <Loader2 size={15} className="spin" /> : <PlayCircle size={15} />} {running ? 'Running…' : 'Run UAT'}
        </button>
      </div>

      {msg && <div style={{ fontSize: 13, color: '#b91c1c' }}>{msg}</div>}

      {report && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Stat label="Passed" value={report.summary.pass} color="#15803d" />
            <Stat label="Failed" value={report.summary.fail} color={report.summary.fail > 0 ? '#b91c1c' : 'var(--color-text-mute)'} />
            <Stat label="Total" value={report.summary.total} color="var(--color-text)" />
          </div>
          <div style={muted}>Ran {new Date(report.ran_at).toLocaleString()} against {report.base_url}</div>

          {report.suites.map(suite => (
            <div key={suite.name} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid var(--color-border)' }}>
                {suite.name}
                <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 12, color: suite.tests.some(t => t.status === 'fail') ? '#b91c1c' : '#15803d' }}>
                  {suite.tests.filter(t => t.status === 'pass').length}/{suite.tests.length}
                </span>
              </div>
              {suite.tests.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderTop: '1px solid var(--color-border)' }}>
                  {icon(t.status)}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{t.id} · {t.name}</div>
                    {t.detail && <div style={{ fontSize: 12, color: t.status === 'fail' ? '#b91c1c' : 'var(--color-text-mute)' }}>{t.detail}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{t.ms}ms</div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {!report && !running && !msg && <div style={{ ...muted, padding: 8 }}>No run yet. Click “Run UAT”.</div>}
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ ...card, minWidth: 90, textAlign: 'center', padding: '10px 16px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: .4 }}>{label}</div>
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 14, background: 'var(--color-surface)' }
const cardTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700 }
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
